/**
 * « Ce qui lui sera proposé » — le plan du soir, avant le soir (§6.10).
 *
 * Demandé par Hedi le 6 septembre 2026 : *« un sous tab pour chaque élève qui me
 * montre les exercices qui lui seront proposés, les compétences que ça couvre et
 * la date à laquelle cette compétence a été rencontrée la première fois »*, puis
 * *« je veux aussi que ça me montre le niveau des exercices »*.
 *
 * **Le même plan que celui de l'enfant, jamais un second calcul.** La route
 * appelle la fonction qui sert l'écran de l'enfant ; un écran parent qui referait
 * le choix finirait par annoncer autre chose que ce que l'enfant reçoit, et rien
 * ne dirait lequel des deux a raison.
 *
 * **Regarder ne consomme pas.** Ce sont les candidats du tirage, pas un tirage :
 * ouvrir l'onglet dix fois ne retire rien de la réserve du soir.
 *
 * **Le corrigé n'y est pas, et ce n'est pas un choix d'affichage** : l'API ne
 * l'envoie pas. C'est le plus exposé des écrans de cette coquille — il montre
 * l'énoncé d'un exercice que l'enfant n'a **pas encore** fait (règle 2).
 *
 * **Deux dates de première rencontre, pas une**, parce que le mot a deux sens :
 * vue en classe, d'après l'école ; travaillée ici, d'après nos observations. Une
 * compétence vue en septembre et jamais travaillée ne se lit pas du tout comme
 * une compétence abordée hier.
 *
 * **Un énoncé découpé dans un ouvrage se montre en image**, demandé par Hedi le
 * 15 septembre 2026 : *« montre aux parents (ce qui sera proposé) et aux enfant
 * le parascolaire coupé au lieu de la transcription »*. L'enfant recevait déjà
 * la page photographiée ; le parent lisait `statement`, c'est-à-dire ce qu'un
 * modèle a transcrit de cette page à l'ingestion — juste, et introuvable dans le
 * cahier que l'enfant a sous la main. Une étiquette nomme l'ouvrage, au-dessus
 * de l'image et **jamais sans elle**. Le corrigé, lui, reste dehors : c'est la
 * même URL présignée que l'enfant reçoit, pour l'énoncé seul.
 *
 * **Le niveau est un mot, pas un score.** La bande de tirage du soir est posée
 * là où le stock vérifié se trouve, très en dessous de ce que le programme
 * attend, si bien qu'un score sur 100 — qui mesure exactement cet écart —
 * vaudrait zéro sur tous ces exercices. Un écran qui afficherait zéro partout ne dirait pas « facile »,
 * il dirait « cassé ».
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type {
  BandeDemandee,
  CompetencePrevue,
  ExercicePrevu,
  TravailPrevu,
} from "@/api/schema";
import { AsseoirACote } from "@/ui/asseoir";
import { EnonceImages, imagesDeLEnonce } from "@/ui/EnonceImage";
import { EtiquetteParascolaire } from "@/ui/Parascolaire";
import { bornes, phraseDePenurie, phraseDuManque } from "@/ui/penurie";
import { enClair } from "@/ui/dates";
import { LiensDImpression } from "@/ui/impression";
import { etiquette } from "@/ui/matieres";
import { phraseSansPreuve, separerParJour } from "@/ui/planDuSoir";
import { Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";
import { TexteMathematique } from "@/ui/TexteMathematique";

const CAPS: Record<string, string> = {
  aujourdhui: "les cours d'aujourd'hui",
  demain: "les cours de demain",
  examen: "le prochain contrôle",
};

/** Les quatre échelons de `mastery.difficulty_step`, dits à un parent. */
export const NIVEAUX: Record<string, string> = {
  consolidation: "consolidation",
  entrainement: "entraînement",
  attendu: "au niveau attendu",
  approfondissement: "approfondissement",
};

/**
 * La bande demandée, et celle réellement fouillée quand elles diffèrent.
 *
 * Les deux, jamais l'une : le plan compte sa réserve sur la première, le tirage
 * puise dans la seconde, et n'afficher que la bande demandée ferait lire « hors
 * bande » sur des exercices que la séance sert le soir même.
 */
export function bandeDemandee(bande: BandeDemandee): string {
  const demandee = bornes(bande.bas, bande.haut);
  if (!bande.marge) return demandee;
  return `${demandee} (élargie à ${fouillee(bande)} au tirage)`;
}

/** La plage réellement fouillée par le tirage, marge comprise. */
const fouillee = (bande: BandeDemandee): string =>
  bornes(bande.bas - (bande.marge ?? 0), bande.haut + (bande.marge ?? 0));

/**
 * Pourquoi cette compétence ne rend aucun énoncé — laquelle des quatre causes.
 *
 * La phrase vit dans `parent/penurie` depuis le 8 septembre 2026 : la carte
 * « Vu en classe, jamais mesuré ici » en est le second lecteur, et deux copies
 * finiraient par nommer la même cause de deux façons dans la même coquille.
 * Ce qui reste ici est ce que seul cet écran sait — la bande fouillée, et le
 * fait que la séance enchaînera.
 */
export function pourquoiRien(competence: CompetencePrevue, plan?: TravailPrevu): string {
  const penurie = competence.penurie;
  const suite = "La séance passera à la compétence suivante.";
  if (!penurie) {
    // Le serveur ne renseigne la cause que sur une liste vide, et alors
    // toujours. Y arriver sans cause veut dire que le contrat a bougé, pas que
    // tout va bien — on ne prétend donc rien.
    return `Aucun exercice à proposer ce soir. ${suite}`;
  }
  return phraseDePenurie(penurie, {
    suite,
    bandeFouillee: plan?.bande_demandee ? fouillee(plan.bande_demandee) : undefined,
  });
}

/** « vue en classe le 2 septembre, jamais travaillée ici ». */
export function rencontre(competence: CompetencePrevue): string {
  const enClasse = competence.premiere_fois_vue_en_classe
    ? `vue en classe le ${enClair(competence.premiere_fois_vue_en_classe)}`
    : "pas encore repérée dans le cahier de textes";
  const ici = competence.premiere_fois_travaillee
    ? `travaillée ici depuis le ${enClair(competence.premiere_fois_travaillee)}`
    : "jamais travaillée ici";
  // L'enfant a buté dessus cette semaine, d'après son journal ou son cahier
  // (lot P5). À côté de « vue en classe », qui est la date à laquelle le
  // parent la rapporte.
  const bute = competence.bloquee ? " · a buté dessus cette semaine" : "";
  return `${enClasse}${bute} · ${ici}`;
}

export function ProchainScreen() {
  const { childId = "" } = useParams();
  const [plan, setPlan] = useState<TravailPrevu | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let annule = false;
    setErreur(null);
    void (async () => {
      try {
        const charge = await call("travailPrevu", { params: { child_id: childId } });
        if (!annule) setPlan(charge);
      } catch (echec) {
        if (!annule) {
          setErreur(echec instanceof ApiError ? echec.message : "Chargement impossible.");
        }
      }
    })();
    return () => {
      annule = true;
    };
  }, [childId]);

  const competences = plan?.competencies ?? [];
  const manque = plan?.manque ?? [];
  const { dabord, ensuite } = separerParJour(competences, plan?.le);

  return (
    <Screen
      title="Ce qui lui sera proposé"
      lead="Le plan de la prochaine séance de travail, tel que l'enfant le recevra."
    >
      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}
      {!plan && !erreur ? <Loading /> : null}

      {plan ? (
        <Card title="Le cap">
          <p className="muted small">
            {CAPS[plan.applique || plan.focus || ""] ?? "le programme en cours"}
            {plan.le ? `, pour le ${enClair(plan.le)}` : ""}
            {plan.examen ? ` · ${plan.examen}` : ""}
          </p>
          {/* Un repli se dit. C'est la seule chose que l'enfant peut vérifier
              lui-même, et la première qui lui apprendrait à ne plus croire
              l'écran si on la taisait. */}
          {plan.repli ? (
            <p className="muted small">
              Le cap réglé était « {CAPS[plan.focus || ""] ?? plan.focus} » : faute de
              données pour ce soir, la séance se rabat sur ce qui est affiché ici.
            </p>
          ) : null}
          {/* Une seule fois, et pas par compétence : c'est le serveur qui pose
              cette bande, la même pour tout le plan. Répétée douze fois, elle
              finirait tôt ou tard par s'afficher différemment d'une carte à
              l'autre. */}
          {plan.bande_demandee ? (
            <p className="muted small">
              Bande de difficulté demandée : {bandeDemandee(plan.bande_demandee)}
            </p>
          ) : null}
          <p className="muted small">
            Ce sont les exercices <strong>parmi lesquels</strong> le tirage se fera.
            Ouvrir cet onglet ne consomme rien de la réserve.
          </p>
        </Card>
      ) : null}

      {/* Ce que le jour visé n'a pas pu donner. Rendu au parent avec ses
          nombres — c'est lui qui doit pouvoir mesurer le trou, et l'écran de
          l'enfant s'en passe par l'invariant §14.1. Hedi, 8 septembre 2026 :
          le cap visait l'interro d'histoire-géo du 10 et le plan affichait de
          la SVT du 15, sans un mot sur la bascule. */}
      {plan && manque.length > 0 ? (
        <Card title={`Le ${plan.le ? enClair(plan.le) : "jour visé"} n'a rien donné`}>
          <p className="muted small">
            Les places du plan sont donc allées aux journées de cours suivantes. Chaque
            ligne ci-dessous porte sa date.
          </p>
          <ul>
            {manque.map((raison) => (
              <li key={raison.matiere} className="muted small">
                {phraseDuManque(raison, true)}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {plan && competences.length === 0 ? (
        <Card>
          <Empty>
            Rien de prévu pour l'instant : ni cours identifié, ni compétence en réserve.
          </Empty>
        </Card>
      ) : null}

      {dabord.map((competence) => (
        <Competence key={competence.competency_id} competence={competence} plan={plan} />
      ))}

      {/* Ce qui ne vient pas du jour visé, dans les places que celui-ci n'a pas
          prises. Rendu à part et daté : rangé à la suite sans rien dire, cela se
          lirait comme du travail pour ce soir, et le parent y verrait un chapitre
          que le professeur n'a pas encore fait. */}
      {ensuite.length > 0 ? (
        <Card title="Si l'enfant a le temps">
          <p className="muted small">
            La séance sert d'abord ce qui précède. Ce qui suit vient d'autres journées
            de cours et ne sera proposé qu'une fois le reste terminé.
          </p>
          {/* Avec le nombre, ici et pas chez l'enfant : le parent est le seul
              des deux à pouvoir en faire quelque chose — déclarer les chapitres
              vus, ou constater qu'un cours n'a pas encore eu lieu. */}
          {(plan?.sans_preuve ?? 0) > 0 ? (
            <p className="muted small">{phraseSansPreuve(plan?.sans_preuve ?? 0, true)}</p>
          ) : null}
        </Card>
      ) : null}

      {ensuite.length === 0 && (plan?.sans_preuve ?? 0) > 0 ? (
        <Card title="Ce que les jours suivants n'ont pas donné">
          <p className="muted small">{phraseSansPreuve(plan?.sans_preuve ?? 0, true)}</p>
        </Card>
      ) : null}

      {ensuite.map((competence) => (
        <Competence key={competence.competency_id} competence={competence} plan={plan} />
      ))}
    </Screen>
  );
}

function Competence({
  competence,
  plan,
}: {
  competence: CompetencePrevue;
  plan: TravailPrevu | null;
}) {
  const exercices = competence.exercises ?? [];
  const bandes = competence.bandes ?? [];
  return (
    <Card title={competence.label}>
      <p className="muted small">
        {etiquette(competence.subject)} · {competence.disponibles ?? 0} exercice
        {(competence.disponibles ?? 0) > 1 ? "s" : ""} en réserve
      </p>
      {/* Où le stock vérifié se trouve vraiment. Le compte ci-dessus se prend
          dans la bande du soir : à zéro, il ne distingue pas une compétence
          sans le moindre exercice d'une compétence dont tout le stock est
          ailleurs sur l'échelle — deux causes opposées, l'une à générer,
          l'autre à régler. Rien du tout : pas de ligne, l'absence est déjà
          l'information et une bande à zéro n'existe pas. */}
      {bandes.length > 0 ? (
        <p className="muted small">
          Stock par bande :{" "}
          {bandes.map((bande, rang) => (
            <span key={`${bande.bas}`} className={bande.servable ? "" : "hors-bande"}>
              {rang > 0 ? " · " : ""}
              {bornes(bande.bas, bande.haut)} ({bande.exercices}
              {bande.servable ? "" : ", hors bande"})
            </span>
          ))}
        </p>
      ) : null}
      <p className="muted small">{rencontre(competence)}</p>
      {competence.pour ? (
        <p className="muted small">Séance du {enClair(competence.pour)}</p>
      ) : null}
      {competence.demandee ? (
        <p className="muted small">Demandé par le cahier de textes : {competence.demandee}</p>
      ) : null}
      {!competence.demandee && competence.vue ? (
        /* Ce qui l'a fait passer devant, depuis le 8 septembre 2026 : une
           compétence dont on a la preuve que la classe l'a traitée passe avant
           celles que seul le découpage théorique de l'année a ouvertes. Un ordre
           sans motif visible n'est pas vérifiable. */
        <p className="muted small">Traité en classe — {competence.vue}.</p>
      ) : null}
      {competence.prepare ? (
        /* « Annoncé » et non « prévu » : c'est le professeur qui l'a écrit, et
           la date est celle où il l'a écrit — pas celle du cours, que le cahier
           de textes ne dit pas. Le chapitre annoncé, lui, n'est pas dans cette
           liste : il n'est pas encore enseigné. */
        <p className="muted small">
          Prépare un chapitre annoncé le {enClair(competence.prepare)} et pas encore traité
          en classe.
        </p>
      ) : null}

      {exercices.length === 0 ? (
        /* Trois causes et non une. « Aucun exercice servable » les confondait
           toutes les trois, et la première — rien de vérifié — appelle une
           génération là où la deuxième appelle un réglage de bande et la
           troisième ne demande rien du tout. */
        <Empty>{pourquoiRien(competence, plan ?? undefined)}</Empty>
      ) : (
        <details className="deep">
          <summary>
            {exercices.length} énoncé{exercices.length > 1 ? "s" : ""} possible
            {exercices.length > 1 ? "s" : ""}
          </summary>
          <ul className="exercices">
            {exercices.map((exercice) => (
              <Enonce key={exercice.exercise_id} exercice={exercice} />
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}

function Enonce({ exercice }: { exercice: ExercicePrevu }) {
  const { childId = "" } = useParams();
  const images = imagesDeLEnonce(exercice.statement_image_urls, exercice.statement_image_url);
  return (
    <li className="exercice">
      {/* L'ouvrage découpé plutôt que sa transcription — Hedi, le 15 septembre
          2026. Le `statement` d'un exercice de livre est ce qu'un modèle a lu de
          la page à l'ingestion : juste, et introuvable dans le cahier posé sur
          la table. L'étiquette accompagne l'image et jamais le texte, faute de
          quoi elle enverrait chercher une page qui n'est pas montrée. */}
      {images.length ? (
        <>
          <EtiquetteParascolaire nom={exercice.parascolaire} />
          <EnonceImages images={images} statement={exercice.statement} />
        </>
      ) : (
        <TexteMathematique className="exercice__enonce">{exercice.statement}</TexteMathematique>
      )}
      <p className="muted small">
        {/* Le niveau, jamais la difficulté brute : celle-ci est une échelle Elo,
            juste et illisible. */}
        {NIVEAUX[exercice.niveau ?? ""] ?? "niveau inconnu"}
      </p>
      {/* Écran du parent, qui imprime à l'avance (Hedi, 27 septembre 2026) :
          le corrigé s'ouvre d'une route de parent, jamais de l'enfant. */}
      {exercice.imprimable ? (
        <LiensDImpression
          cible={{ quoi: "exercice", childId, exerciseId: exercice.exercise_id }}
          corrige={Boolean(exercice.corrige_imprimable)}
        />
      ) : null}
      {/* Faire cet exercice du soir ensemble, le parent tenant le rôle du
          tuteur (lot P4) : indices un à un, corrigé après une réponse. */}
      <p>
        <AsseoirACote childId={childId} cible={{ exerciseId: exercice.exercise_id }} />
      </p>
    </li>
  );
}
